/**
 * DemoCampaign - Public page for generating a campaign without registration
 * 3 fields → AI generates full Campaign Pack → CTA to sign up
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Copy, CheckCircle2, Facebook, Instagram,
  Target, Lightbulb, Hash, ArrowRight, Zap, MapPin, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

const CARD_CONFIG = {
  facebook_post: { icon: Facebook, color: '#1877F2', bg: '#EBF5FF' },
  instagram_caption: { icon: Instagram, color: '#E4405F', bg: '#FDE8EC' },
  cta: { icon: Target, color: '#10B981', bg: '#D1FAE5' },
  promo_idea: { icon: Lightbulb, color: '#F59E0B', bg: '#FEF3C7' },
  hashtags: { icon: Hash, color: '#8B5CF6', bg: '#EDE9FE' },
};

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-colors hover:bg-gray-100"
      style={{ color: copied ? '#10B981' : '#94A3B8', border: '1px solid #E2E8F0' }}>
      {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Skopiowano' : 'Kopiuj'}
    </button>
  );
}

export default function DemoCampaign() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [city, setCity] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!name.trim()) { toast.error('Podaj nazwę firmy'); return; }
    if (!industry.trim()) { toast.error('Podaj branżę'); return; }
    setGenerating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/demo/generate`, {
        business_name: name, industry, city,
      });
      setResult(res.data);
      toast.success('Kampania wygenerowana!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd generowania');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF8' }} data-testid="demo-page">
      {/* Top bar */}
      <div className="py-3 px-4 flex items-center justify-between" style={{ background: 'white', borderBottom: '1px solid #E5E7EB' }}>
        <a href="/" className="flex items-center gap-1.5 text-base font-bold" style={{ color: '#F7931E' }}>
          <Sparkles className="w-5 h-5" /> ApisAI
        </a>
        <a href="/rejestracja" className="px-4 py-1.5 rounded-xl text-xs font-semibold text-white" style={{ background: '#F7931E' }}
          data-testid="signup-top-btn">
          Załóż konto
        </a>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Hero */}
        {!result && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold mb-4"
              style={{ background: '#FEF3E2', color: '#F7931E' }}>
              <Zap className="w-3 h-3" /> Bez rejestracji · Za darmo
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: '#0F172A' }}>
              Stwórz kampanię w 30 sekund
            </h1>
            <p className="text-base" style={{ color: '#64748B' }}>
              AI wygeneruje posty, grafiki i treści marketingowe dla Twojej firmy
            </p>
          </motion.div>
        )}

        {/* Form */}
        {!result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="rounded-2xl border p-6 mb-6" style={{ background: 'white', borderColor: '#E5E7EB' }}
            data-testid="demo-form">
            <div className="space-y-4 mb-5">
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: '#64748B' }}>Nazwa firmy / hodowli *</label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#CBD5E1' }} />
                  <input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="np. Hodowla Jeta, Salon Urody Anna"
                    className="w-full rounded-xl border pl-10 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                    style={{ borderColor: '#E5E7EB' }}
                    data-testid="demo-name" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: '#64748B' }}>Branża *</label>
                <div className="relative">
                  <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#CBD5E1' }} />
                  <input value={industry} onChange={(e) => setIndustry(e.target.value)}
                    placeholder="np. hodowla psów, fryzjer, restauracja"
                    className="w-full rounded-xl border pl-10 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                    style={{ borderColor: '#E5E7EB' }}
                    data-testid="demo-industry" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: '#64748B' }}>Miasto</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#CBD5E1' }} />
                  <input value={city} onChange={(e) => setCity(e.target.value)}
                    placeholder="np. Warszawa"
                    className="w-full rounded-xl border pl-10 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                    style={{ borderColor: '#E5E7EB' }}
                    data-testid="demo-city" />
                </div>
              </div>
            </div>
            <Button onClick={handleGenerate} disabled={generating}
              className="w-full rounded-2xl h-13 font-bold text-white text-base"
              style={{ background: '#F7931E', height: '52px' }}
              data-testid="demo-generate-btn">
              {generating ? (
                <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generuję kampanię...</>
              ) : (
                <><Sparkles className="w-5 h-5 mr-2" /> Generuj kampanię</>
              )}
            </Button>
          </motion.div>
        )}

        {/* Results */}
        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {/* Badge */}
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="text-center mb-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold mb-3"
                  style={{ background: '#F0FDF4', color: '#10B981' }}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Kampania gotowa!
                </div>
                <h2 className="text-2xl font-bold mb-1" style={{ color: '#0F172A' }}>
                  {result.business_name}
                </h2>
                <p className="text-sm" style={{ color: '#64748B' }}>
                  {result.industry} · {result.city || 'Polska'}
                </p>
              </motion.div>

              {/* Cards */}
              <div className="space-y-3 mb-6" data-testid="demo-results">
                {result.cards.map((card, i) => {
                  const cfg = CARD_CONFIG[card.type] || CARD_CONFIG.cta;
                  const Icon = cfg.icon;
                  return (
                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.08 }}
                      className="rounded-2xl border overflow-hidden" style={{ background: 'white', borderColor: '#E5E7EB' }}>
                      {card.image_url && (
                        <img src={card.image_url} alt="" className="w-full h-44 object-cover" />
                      )}
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: cfg.bg }}>
                              <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                            </div>
                            <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>{card.title}</span>
                          </div>
                          <CopyBtn text={card.content} />
                        </div>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#374151' }}>
                          {card.content}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* CTA - Sign up */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="rounded-2xl overflow-hidden mb-6"
                style={{ background: 'linear-gradient(135deg, #F97316, #F59E0B)' }}
                data-testid="demo-cta">
                <div className="px-6 py-8 text-center">
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                    Twoja kampania jest gotowa!
                  </h2>
                  <p className="text-white/85 text-sm mb-5 max-w-sm mx-auto">
                    Załóż darmowe konto, aby:
                  </p>
                  <div className="flex flex-col items-center gap-1.5 mb-6 text-white/90 text-sm">
                    <span><CheckCircle2 className="w-4 h-4 inline mr-1.5" />opublikować post na Facebooku</span>
                    <span><CheckCircle2 className="w-4 h-4 inline mr-1.5" />zaplanować publikację</span>
                    <span><CheckCircle2 className="w-4 h-4 inline mr-1.5" />generować kolejne kampanie</span>
                  </div>
                  <a href="/rejestracja"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl text-base font-bold transition-transform hover:scale-105"
                    style={{ background: 'white', color: '#F97316' }}
                    data-testid="demo-signup-btn">
                    Załóż darmowe konto <ArrowRight className="w-5 h-5" />
                  </a>
                  <p className="text-white/50 text-xs mt-3">Bez karty kredytowej</p>
                </div>
              </motion.div>

              {/* Generate another */}
              <div className="text-center">
                <button onClick={() => setResult(null)}
                  className="text-sm font-medium hover:underline" style={{ color: '#F7931E' }}
                  data-testid="try-again-btn">
                  Wygeneruj nową kampanię
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="text-center py-6 border-t" style={{ borderColor: '#E5E7EB' }}>
        <a href="/" className="inline-flex items-center gap-1 text-sm font-bold" style={{ color: '#F7931E' }}>
          <Sparkles className="w-4 h-4" /> ApisAI
        </a>
        <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>AI Marketing Manager</p>
      </div>
    </div>
  );
}
